import { useState } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { InputOTP, InputOTPGroup, InputOTPSlot } from "@/components/ui/input-otp";
import { useInviteVerification } from "@/hooks/useInviteVerification";
import { Loader2 } from "lucide-react";

interface InviteVerificationFormProps {
  token: string;
  onVerified: (invitation: any) => void;
}

export function InviteVerificationForm({ token, onVerified }: InviteVerificationFormProps) {
  const [code, setCode] = useState("");
  const { verifyCode, isVerifying } = useInviteVerification();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (code.length !== 6) return;

    const { success, invitation } = await verifyCode(token, code);
    if (success && invitation) {
      onVerified(invitation);
    }
  };

  const handleCodeChange = (value: string) => {
    setCode(value);
    if (value.length === 6) {
      // Auto-submit when 6 digits are entered
      verifyCode(token, value).then(({ success, invitation }) => {
        if (success && invitation) {
          onVerified(invitation);
        }
      });
    }
  };

  return (
    <Card className="w-full max-w-md">
      <CardHeader className="space-y-1">
        <CardTitle className="text-2xl font-bold">Verify Your Invitation</CardTitle>
        <CardDescription>
          Enter the 6-digit verification code sent to your email
        </CardDescription>
      </CardHeader>
      <CardContent>
        <form onSubmit={handleSubmit} className="space-y-6">
          <div className="flex justify-center">
            <InputOTP
              maxLength={6}
              value={code}
              onChange={handleCodeChange}
              disabled={isVerifying}
            >
              <InputOTPGroup>
                <InputOTPSlot index={0} />
                <InputOTPSlot index={1} />
                <InputOTPSlot index={2} />
                <InputOTPSlot index={3} />
                <InputOTPSlot index={4} />
                <InputOTPSlot index={5} />
              </InputOTPGroup>
            </InputOTP>
          </div>

          <Button
            type="submit"
            className="w-full"
            disabled={code.length !== 6 || isVerifying}
          >
            {isVerifying ? (
              <>
                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                Verifying...
              </>
            ) : (
              "Verify Code"
            )}
          </Button>
        </form>
      </CardContent>
    </Card>
  );
}
